import React, { useState, useMemo } from 'react';
import './MultipleChoiceQuestion.css';
import { stripHtml } from '@components/common/RichText';

/**
 * Matching Question Component
 * Match items from left column to right column
 */
export function MatchingQuestion({ question, value = {}, onChange, disabled = false, showCorrect = false }) {
  const [selectedLeft, setSelectedLeft] = useState(null);

  const { leftItems, rightItems } = useMemo(() => {
    if (!question?.leftItems || !question?.rightItems) {
      return { leftItems: [], rightItems: [] };
    }
    
    const right = [...question.rightItems];
    // Shuffle right items only
    for (let i = right.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [right[i], right[j]] = [right[j], right[i]];
    }
    return { leftItems: question.leftItems, rightItems: right };
  }, [question?.id, question?.leftItems, question?.rightItems]);

  const handleLeftItemClick = (leftId) => {
    if (disabled) return;
    setSelectedLeft(prevSelected => {
      if (prevSelected === leftId) return null;
      return leftId;
    });
  };

  const handleRightItemClick = (rightId) => {
    if (disabled) return;
    if (!selectedLeft) return;

    const userPairs = typeof value === 'object' && value !== null ? { ...value } : {};
    userPairs[selectedLeft] = rightId;
    onChange(userPairs);
    setSelectedLeft(null);
  };

  const correctPairs = question?.correctAnswer || {};

  const getMatchStatus = (leftId, rightId) => {
    if (!showCorrect) return '';
    const matched = value[leftId] === rightId;
    const correct = correctPairs[leftId] === rightId;
    
    if (matched && correct) return 'match-correct';
    if (matched && !correct) return 'match-incorrect';
    return '';
  };

  const getRightText = (rightId) => {
    const match = question?.rightItems?.find((item) => item.id === rightId);
    const label = stripHtml(match?.text || '').trim();
    return label || 'selected option';
  };

  return (
    <div className="matching-question">
      <div className="mb-3 text-muted small">
        <i className="bi bi-info-circle"></i> Click an item on the left, then click its match on the right
      </div>
      <div className="matching-columns">
        <div className="matching-column">
          <h6>Items to Match</h6>
          {leftItems.map((item) => {
            const isSelected = selectedLeft === item.id;
            const isMatched = value[item.id] !== undefined;
            const currentRightId = value[item.id];
            const matchStatus = currentRightId ? getMatchStatus(item.id, currentRightId) : '';
            
            return (
              <div
                key={item.id}
                className={`matching-item ${isSelected ? 'selected' : ''} ${isMatched ? 'matched' : ''} ${matchStatus} ${disabled ? 'disabled' : ''}`}
                onClick={() => handleLeftItemClick(item.id)}
                role="button"
                tabIndex={disabled ? -1 : 0}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <span
                    className="matching-rich-content"
                    dangerouslySetInnerHTML={{ __html: item.text || '<span class="text-muted">Untitled item</span>' }}
                  />
                  {currentRightId && (
                    <span className="match-chip">
                      → {getRightText(currentRightId)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="matching-column">
          <h6>Matching Options</h6>
          {rightItems.map((item) => {
            const isMatched = Object.values(value).includes(item.id);
            
            return (
              <div
                key={item.id}
                className={`matching-item ${isMatched ? 'matched' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => handleRightItemClick(item.id)}
                role="button"
                tabIndex={disabled ? -1 : 0}
              >
                <span
                  className="matching-rich-content"
                  dangerouslySetInnerHTML={{ __html: item.text || '<span class="text-muted">Untitled option</span>' }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
